import { format } from "date-fns";
import { StoreSlice } from "../types/StoreSlice";
import NitroService from "../utils/nitro.service";
import cookie from "./cookie";
import { IFeatureFlags } from "../types/IFeatureFlags";
import { IPinExtension } from "../types/ILocation";
import { IFavoriteOriginPricesResponse } from "../types/IFavoriteOrigins";

const defaultPartyTypes = ["koch", "competitor", "customer"];
const defaultMonth = format(new Date(), "yyyy-MM-dd");

export interface IFilterSlice {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  activeFilter: string | null;
  setActiveFilter: (activeFilter: string | null) => void;

  // ##########################################################################################
  // ## Filters ###############################################################################
  // ##########################################################################################
  uom: string;
  setUom: (uom: string) => void;

  currency: string;
  setCurrency: (currency: string) => void;

  featureFlags: IFeatureFlags;
  setFeatureFlags: (flags: IFeatureFlags) => void;

  priorityProducts: string[];
  setPriorityProducts: (priorityProducts: string[]) => void;

  product: string;
  setProduct: (product: string) => void;
  mode: string;
  setMode: (mode: string) => void;
  partyTypes: string[];
  setPartyTypes: (value: string[]) => void;
  currentFilters: {
    party: string;
    city: string;
    state: string;
    location: string;
    month: string;
  };
  previousFilters: {
    party: string;
    city: string;
    state: string;
    location: string;
    month: string;
  };

  locations: IPinExtension[];
  loadLocations: (options?: {
    notificationsEnabled?: boolean;
    panTo?: boolean;
  }) => void;
  clearLocations: () => void;
  addLocation: (l: IPinExtension) => void;

  setFilter: (
    property: "party" | "city" | "state" | "location" | "month",
    value: string | string[],
  ) => void;
  setFilters: (filters: any) => void;
  resetFilters: () => void;

  hidePins: boolean;
  setHidePins: (value: boolean) => void;

  // ##########################################################################################
  // ## Filter Side Effects ###################################################################
  // ##########################################################################################
  searchStarted: boolean;
  setSearchStarted: (searchStarted: boolean) => void;

  guidanceLocation: string;
  setGuidanceLocation: (guidanceLocation: string) => void;
  openGuidance: ({ location }: { location: string }) => void;
  fetchFavoriteOrigins: boolean;
  setFetchFavoriteOrigins: (fetchFavoriteOrigins: boolean) => void;
  favoriteOriginsByUser: IFavoriteOriginPricesResponse[];
  favoriteToggleOverrides: Record<string, { truck?: boolean; rail?: boolean }>;
  setFavoriteToggleOverride: (
    id: string,
    overrides: { truck?: boolean; rail?: boolean } | null,
  ) => void;
  setFavoriteOriginsByUser: (
    favoriteOrigins: IFavoriteOriginPricesResponse[],
  ) => void;
  refetchSurroundingLocations: number;
  triggerRefetchSurroundingLocations: () => void;
  selectedMobileDestination: string | null;
  setSelectedMobileDestination: (destination: string | null) => void;
  showHidePinsPopup: boolean;
  setShowHidePinsPopup: (value: boolean) => void;
  logisticsUpdatedModes: { destId: string; modes: any } | null;
  setLogisticsUpdatedModes: (
    data: { destId: string; modes: any } | null,
  ) => void;

  // detect if the user has unsaved edits in the origin manager, and if so, prompt them to save or discard changes before navigating away
  originManagerHasUnsavedPriceEdits: boolean;
  setOriginManagerHasUnsavedPriceEdits: (value: boolean) => void;

  originManagerUnsavedNavAttemptNonce: number;
  originManagerUnsavedNavAttemptSource: "search" | "close" | null;
  triggerOriginManagerUnsavedNavAttempt: (source: "search" | "close") => void;
  originManagerUnsavedNavResolutionNonce: number;
  originManagerUnsavedNavResolutionAction: "save" | "discard" | null;
  triggerOriginManagerUnsavedNavResolution: (
    action: "save" | "discard",
  ) => void;
  originManagerCloseRequestNonce: number;
  triggerOriginManagerCloseRequest: () => void;
  originManagerIsSyncing: boolean;
  setOriginManagerIsSyncing: (value: boolean) => void;
  originManagerLiveSplitMonthKeysByProduct: Record<string, string[]>;
  setOriginManagerLiveSplitMonthKeys: (
    product: string,
    monthKeys: string[],
  ) => void;
  clearOriginManagerLiveSplitMonthKeys: (product: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createFilterSlice: StoreSlice<IFilterSlice> = (set, get) => {
  const initialState = {
    isLoading: true,

    activeFilter: null,

    // ##########################################################################################
    // ## Filters ###############################################################################
    // ##########################################################################################
    uom: cookie.uom,
    currency: cookie.currency,
    priorityProducts: cookie.products,

    product: "",
    mode: "TRUCK",
    partyTypes: defaultPartyTypes,
    currentFilters: {
      party: "",
      city: "",
      state: "",
      location: "",
      month: defaultMonth,
    },
    previousFilters: {
      party: "",
      partyTypes: defaultPartyTypes,
      city: "",
      state: "",
      location: "",
      month: defaultMonth,
    },

    locations: [],

    hidePins: false,

    // ##########################################################################################
    // ## Filter Side Effects ###################################################################
    // ##########################################################################################
    searchStarted: false,

    guidanceLocation: "",
    fetchFavoriteOrigins: true,
    favoriteOriginsByUser: [],
    favoriteToggleOverrides: {},
    refetchSurroundingLocations: 0,
    showHidePinsPopup: true,
    logisticsUpdatedModes: null,
    originManagerHasUnsavedPriceEdits: false,
    originManagerUnsavedNavAttemptNonce: 0,
    originManagerUnsavedNavAttemptSource: null,
    originManagerUnsavedNavResolutionNonce: 0,
    originManagerUnsavedNavResolutionAction: null,
    originManagerCloseRequestNonce: 0,
    originManagerIsSyncing: false,
    originManagerLiveSplitMonthKeysByProduct: {},
  };

  return {
    ...initialState,
    setIsLoading: (isLoading: boolean) => {
      set({ isLoading });
    },

    setActiveFilter: (activeFilter: string | null) => {
      set({ activeFilter });
    },

    // ##########################################################################################
    // ## Filters ###############################################################################
    // ##########################################################################################

    // TODO: prefer local storage for user settings but currently using cookies
    setUom: (uom: string) => {
      cookie.setUom(uom);
      set({ uom });
    },
    setCurrency: (currency: string) => {
      cookie.setCurrency(currency);
      set({ currency });
    },
    setPriorityProducts: (priorityProducts: string[]) => {
      cookie.setProducts(priorityProducts);
      const { product } = get() as any;
      // keep selected product in sync in case it was just removed from favorites
      const nextProduct = priorityProducts.includes(product)
        ? product
        : priorityProducts[0] || "";
      set({ priorityProducts, product: nextProduct });
    },

    featureFlags: {
      manageRailRate: true,
      originManager: true,
      multiRailStation: true,
    },
    setFeatureFlags: (flags: IFeatureFlags) => set({ featureFlags: flags }),

    loadLocations: async (options?: {
      notificationsEnabled?: boolean;
      panTo?: boolean;
    }) => {
      const { currentFilters, product, partyTypes } = get() as any;
      const { party, city, state, location, month } = currentFilters;
      const { showCompetitiveAnalysisModal } = get() as any;
      let searchStarted = false;

      if (party || city || state || location) {
        searchStarted = true;
      }
      set({
        isLoading: true,
        searchStarted,
      } as any);

      const stateRequired = !party && !state && !city && !location;

      // On mobile with multiLocationQuote, if no search is active, don't load search locations
      // this is to fix favorite origin pins disappearing issue
      const isMobile = window.innerWidth <= 768;
      if (isMobile && stateRequired) {
        set({ isLoading: false } as any);
        return;
      }

      if (stateRequired) {
        set({ locations: [], isLoading: false });
        return;
      }

      const modifiedPartyTypes = partyTypes;
      if (modifiedPartyTypes.length === 0) {
        modifiedPartyTypes.push("koch");
        modifiedPartyTypes.push("competitor");
        modifiedPartyTypes.push("customer");
      }

      const queryParams = {
        logisticsProductGroupName: product,
        partyId: party,
        cityStateId: city,
        stateOrProvinceId: state,
        shipDate: month,
        locationName: location,
        partyTypes: defaultPartyTypes // No longer applying partyType filter at the api level. Pins that don't match current PartyTypes will be opaque.
          .map((r: string) => r.charAt(0).toUpperCase() + r.slice(1))
          .join(","),
      };
      const records = await NitroService.getPins(queryParams, 1, 100);
      const sortedRecords = [
        ...records.filter((r) => r.pinType === "koch"),
        ...records.filter((r) => r.pinType === "kochAndCompetitor"),
        ...records.filter((r) => r.pinType === "competitor"),
        ...records.filter((r) => r.pinType === "customer"),
      ];

      const count = sortedRecords.length;
      const updatedState: any = {
        locations: sortedRecords,
        storageFacilityCityWarning: false,
        storageFacilityStateWarning: false,
        isLoading: false,
      };

      // On mobile with multiLocationQuote, if there are no search results but there are
      // existing locations (favorite origins), don't overwrite them
      const isMobileDevice = window.innerWidth <= 768;
      const currentLocations = get().locations || [];
      const { showMobileSearchPanel } = get() as any;

      const hasFavoriteOrigins =
        currentLocations.length > 0 &&
        currentLocations.some(
          (loc) =>
            loc.truckMaxPrice !== undefined || loc.railMaxPrice !== undefined,
        );

      if (isMobileDevice && hasFavoriteOrigins && !showMobileSearchPanel) {
        updatedState.locations = currentLocations;
      } else if (
        isMobileDevice &&
        sortedRecords.length === 0 &&
        currentLocations.length > 0
      ) {
        updatedState.locations = currentLocations;
      }

      if (!showCompetitiveAnalysisModal) {
        updatedState.selectedLocation = undefined;
      }
      if (options?.notificationsEnabled) {
        if (party && count === 0) {
          if (city) {
            updatedState.storageFacilityCityWarning = true;
          } else if (state) {
            updatedState.storageFacilityStateWarning = true;
          }
        }
      }

      set(updatedState as any);

      if (options?.panTo) {
        const { updatePanToBounds } = get() as any;
        updatePanToBounds(currentFilters, updatedState.locations);
      }
    },

    clearLocations: () => {
      set({ locations: [] });
    },

    addLocation: (location: IPinExtension) => {
      const { locations } = get();

      set({
        locations: [...locations, location],
      });
    },

    setProduct: (product: string) => {
      set({ product: product.trim().toUpperCase() });
    },

    setMode: (mode: string) => {
      set({ mode });
    },

    setFavoriteToggleOverride: (
      id: string,
      overrides: { truck?: boolean; rail?: boolean } | null,
    ) => {
      set((state: any) => {
        const next = { ...(state.favoriteToggleOverrides || {}) };
        if (!overrides) {
          delete next[id];
        } else {
          next[id] = { ...(next[id] || {}), ...overrides };
        }
        return { favoriteToggleOverrides: next } as any;
      });
    },

    setPartyTypes: (partyTypes: string[]) => {
      set({ partyTypes });
    },

    setFilter: (
      property: "party" | "city" | "state" | "location" | "month",
      value: string | string[],
    ) => {
      const { currentFilters, partyTypes } = get();
      const { state, city, location } = currentFilters;

      const newFilters = {
        ...currentFilters,
      };
      newFilters[property] = value as any;

      const newState = {
        showAddPopup: false,
        showReviewPopup: false,
        currentFilters: newFilters,
        partyTypes,
        previousFilters: { ...currentFilters },
        selectedLocation: undefined,
        showCompetitiveAnalysisModal: false,
      };

      // Set month to default if invalid or in the past
      if (property === "month") {
        const month = value as string;
        if (Number.isNaN(Date.parse(month))) {
          newState.currentFilters.month = defaultMonth;
        }

        if (new Date(month) < new Date(defaultMonth)) {
          newState.currentFilters.month = defaultMonth;
        }
      }

      if (property === "party") {
        // Show customer pins when Party is selected
        if (value) {
          if (partyTypes.indexOf("customer") === -1) {
            partyTypes.push("customer");
          }

          newState.partyTypes = partyTypes;
        } else {
          if (!state && !city && !location) {
            if (partyTypes.indexOf("customer") > -1) {
              partyTypes.splice(partyTypes.indexOf("customer"), 1);
            }
          }

          newState.partyTypes = partyTypes;
        }
      }

      set(newState);
    },

    setFilters: (filters: {
      party: string;
      city: string;
      state: string;
      location: string;
      month: string;
    }) => {
      const { setLabelType, loadLocations, currentFilters } = get() as any;

      if (!!filters.party && !!filters.state) {
        setLabelType("city");
      } else if (filters.city) {
        setLabelType("location");
      } else if (filters.party) {
        setLabelType("city");
      } else {
        setLabelType("");
      }

      const newFilters = {
        ...currentFilters,
        ...filters,
      };

      set({
        currentFilters: newFilters,
        previousFilters: {
          ...currentFilters,
        },
        showAddPopup: false,
        showReviewPopup: false,
      } as any);
      loadLocations({ notificationsEnabled: true, panTo: true });
    },

    resetFilters: () => {
      const { currentFilters } = get();

      set({
        previousFilters: { ...currentFilters },
        currentFilters: {
          party: "",
          city: "",
          state: "",
          location: "",
          month: defaultMonth,
        },
      });
    },

    setHidePins: (value: boolean) => {
      set({ hidePins: value });
    },

    // ##########################################################################################
    // ## Filter Side Effects ###################################################################
    // ##########################################################################################
    setSearchStarted: (searchStarted: boolean) => {
      set({ searchStarted });
    },

    setGuidanceLocation: (guidanceLocation: string) => {
      set({ guidanceLocation });
    },

    openGuidance: ({ location }) => {
      set({
        guidanceLocation: location,
        showGuidancePanel: true,
      } as any);
    },
    setFetchFavoriteOrigins: (fetchFavoriteOrigins: boolean) => {
      set({ fetchFavoriteOrigins });
    },
    setFavoriteOriginsByUser: (favoriteOrigins: any[]) => {
      set({ favoriteOriginsByUser: favoriteOrigins });
    },
    triggerRefetchSurroundingLocations: () => {
      set((state) => ({
        refetchSurroundingLocations: state.refetchSurroundingLocations + 1,
      }));
    },
    selectedMobileDestination: null,
    setSelectedMobileDestination: (destination: string | null) => {
      set({ selectedMobileDestination: destination });
    },
    setShowHidePinsPopup: (value: boolean) => {
      set({ showHidePinsPopup: value });
    },
    setLogisticsUpdatedModes: (data) => {
      set({ logisticsUpdatedModes: data });
    },

    setOriginManagerHasUnsavedPriceEdits: (value: boolean) => {
      set({ originManagerHasUnsavedPriceEdits: value });
    },
    triggerOriginManagerUnsavedNavAttempt: (source) => {
      set((state) => ({
        originManagerUnsavedNavAttemptSource: source,
        originManagerUnsavedNavAttemptNonce:
          state.originManagerUnsavedNavAttemptNonce + 1,
      }));
    },
    triggerOriginManagerUnsavedNavResolution: (action) => {
      set((state) => ({
        originManagerUnsavedNavResolutionAction: action,
        originManagerUnsavedNavResolutionNonce:
          state.originManagerUnsavedNavResolutionNonce + 1,
      }));
    },
    triggerOriginManagerCloseRequest: () => {
      set((state) => ({
        originManagerCloseRequestNonce:
          state.originManagerCloseRequestNonce + 1,
      }));
    },
    setOriginManagerIsSyncing: (value: boolean) => {
      set({ originManagerIsSyncing: value });
    },
    setOriginManagerLiveSplitMonthKeys: (product, monthKeys) => {
      const normalizedProduct = (product || "").toUpperCase();
      if (!normalizedProduct) {
        return;
      }

      const uniqueMonthKeys = Array.from(
        new Set(
          (monthKeys || []).filter(
            (key): key is string => typeof key === "string" && key.length >= 7,
          ),
        ),
      );

      set((state) => ({
        originManagerLiveSplitMonthKeysByProduct: {
          ...state.originManagerLiveSplitMonthKeysByProduct,
          [normalizedProduct]: uniqueMonthKeys,
        },
      }));
    },
    clearOriginManagerLiveSplitMonthKeys: (product) => {
      const normalizedProduct = (product || "").toUpperCase();
      if (!normalizedProduct) {
        return;
      }

      set((state) => {
        const next = { ...state.originManagerLiveSplitMonthKeysByProduct };
        delete next[normalizedProduct];
        return {
          originManagerLiveSplitMonthKeysByProduct: next,
        };
      });
    },
  };
};
